import { ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { collection, getDocs, orderBy, query, Timestamp, where } from "firebase/firestore";
import { Link } from "react-router-dom";
import { ActionIcon, Badge, Box, Button, Checkbox, Group, Modal, Radio, SegmentedControl, SimpleGrid, Stack, Table, Text, ThemeIcon, Tooltip } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconAlertTriangle, IconChevronsRight, IconClock, IconEye } from "@tabler/icons-react";
import { DatePickerInput } from "@mantine/dates";
import { TagBadge } from "../../components/tag_badge";
import { CtcPurchaseOrder } from "../../types";
import { CtcPoStatusCode, CtcRole, ctcKuwaitDayEnd, isTerminal } from "../../../../shared/ctcLifecycle";
import { MyUserDataContext } from "../../contexts";
import { useFirestore } from "../../helpers/firebaseContext";
import { CTC_NEXT_STEP_ACTIONS, CtcUser, ctcActionsFor, ctcStatusTag, formatMoney, useCtcUser } from "./ctc_helpers";
import { useLiveCollection } from "./useLive";
import { CtcCustomer } from "../../types";

/*
 * Pieces shared by the Firestore and Typesense versions of the Watch list and Purchase orders
 * (ctc_orders.tsx, ctc_orders_search.tsx): the orders table, the View window (columns + sort, remembered
 * per list in this browser) and filters kept while the app is open.
 */

export type Stamp = { toDate?: () => Date; toMillis?: () => number } | undefined;
export const formatStamp = (ts: Stamp) => ts?.toDate?.().toLocaleString("en-GB", { hour12: true }) ?? "";

export const useDebounced = <T,>(value: T, ms = 400) => {
    const [v, setV] = useState(value);
    useEffect(() => {
        const t = setTimeout(() => setV(value), ms);
        return () => clearTimeout(t);
    }, [value, ms]);
    return v;
};

/** List filters kept while the app is open, so coming back from an order shows the same list. */
export const listMemory = new Map<string, unknown>();
export const useMemoryState = <T,>(key: string, initial: T) => {
    const [value, setValue] = useState<T>(() => (listMemory.has(key) ? listMemory.get(key) as T : initial));
    return [value, (v: T) => { listMemory.set(key, v); setValue(v); }] as const;
};

/* ---------- View: columns + sort (per list, remembered in this browser) ---------- */

export type ColumnKey = "beneficiary" | "amount" | "status" | "state" | "lane" | "dealer" | "created" | "updated";
export const COLUMNS: { key: ColumnKey; label: string }[] = [
    { key: "beneficiary", label: "Beneficiary" },
    { key: "amount", label: "Amount" },
    { key: "status", label: "Status" },
    { key: "state", label: "State" },
    { key: "lane", label: "Awaiting action from" },
    { key: "dealer", label: "Dealer" },
    { key: "created", label: "Created" },
    { key: "updated", label: "Updated" },
];
export const ALL_COLUMNS = COLUMNS.map((c) => c.key);

export type SortField = "reference" | "beneficiary" | "amount" | "created" | "updated";
export type SortDir = "asc" | "desc";
export interface ListSort { field: SortField; dir: SortDir }
export const SORT_FIELDS: { value: SortField; label: string; path: string }[] = [
    { value: "reference", label: "Reference", path: "referenceNumber" },
    { value: "beneficiary", label: "Beneficiary", path: "beneficiary.number" },
    { value: "amount", label: "Amount", path: "amount" },
    { value: "created", label: "Created", path: "created" },
    { value: "updated", label: "Updated", path: "updated" },
];
export const sortPath = (field: SortField) => SORT_FIELDS.find((f) => f.value === field)!.path;
export const sameSort = (a: ListSort, b: ListSort) => a.field === b.field && a.dir === b.dir;

export const sortValue = (po: CtcPurchaseOrder, field: SortField): string | number | undefined => {
    switch (field) {
        case "reference": return po.referenceNumber;
        case "beneficiary": return po.beneficiary?.number;
        case "amount": return po.amount;
        case "created": return (po.created as Stamp)?.toMillis?.();
        case "updated": return (po.updated as Stamp)?.toMillis?.();
    }
};
const isEmpty = (v: unknown) => v === undefined || v === null || v === "";
/** In-browser sort (watch list, which is loaded whole). Empty values last; numbers in text compare as numbers. */
export const sortRows = (rows: CtcPurchaseOrder[], { field, dir }: ListSort) => [...rows].sort((a, b) => {
    const x = sortValue(a, field);
    const y = sortValue(b, field);
    if (isEmpty(x) || isEmpty(y)) return isEmpty(x) === isEmpty(y) ? 0 : isEmpty(x) ? 1 : -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : `${x}`.localeCompare(`${y}`, undefined, { numeric: true });
    return dir === "asc" ? c : -c;
});

export const readPref = <T,>(key: string, fallback: T, valid: (v: unknown) => boolean): T => {
    try {
        const v = JSON.parse(localStorage.getItem(key) ?? "null");
        return v !== null && valid(v) ? v as T : fallback;
    } catch {
        return fallback;
    }
};
export const writePref = (key: string, value: unknown) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable: just don't remember */ }
};

/** Shown columns and sort of one list ("orders" / "watch"), restored from localStorage. */
export const useListView = (list: string, defaultSort: ListSort) => {
    const key = `ctcList:${list}:view`;
    const [view, setView] = useState<{ columns: ColumnKey[]; sort: ListSort }>(() => readPref(key, { columns: ALL_COLUMNS, sort: defaultSort }, (v) => {
        const o = v as { columns?: unknown; sort?: ListSort };
        return Array.isArray(o?.columns) && o.columns.every((k) => ALL_COLUMNS.includes(k as ColumnKey)) &&
            SORT_FIELDS.some((f) => f.value === o.sort?.field) && ["asc", "desc"].includes(o.sort?.dir ?? "");
    }));
    const apply = (next: { columns: ColumnKey[]; sort: ListSort }) => { setView(next); writePref(key, next); };
    return { ...view, apply, defaultSort, isDefaultSort: sameSort(view.sort, defaultSort) };
};
export type ListView = ReturnType<typeof useListView>;

export type DayRange = [string | null, string | null];

/**
 * One button → modal: tick the columns to show, choose one sort field and direction, and (lists that support it)
 * a created-date range, then Apply.
 */
export function ListViewButton({ view, note, onApply, dateRange }: {
    view: ListView; note?: string; onApply?: (sortChanged: boolean) => void;
    dateRange?: { value: DayRange; onChange: (v: DayRange) => void };
}) {
    const [open, setOpen] = useState(false);
    const [columns, setColumns] = useState<ColumnKey[]>(view.columns);
    const [sort, setSort] = useState<ListSort>(view.sort);
    const [range, setRange] = useState<DayRange>(dateRange?.value ?? [null, null]);
    const label = SORT_FIELDS.find((f) => f.value === view.sort.field)?.label;
    const hidden = ALL_COLUMNS.length - view.columns.length;
    const dated = !!(dateRange?.value[0] || dateRange?.value[1]);
    return (
        <>
            <Button variant={dated ? "light" : "default"} leftSection={<IconAdjustmentsHorizontal size={16} />}
                onClick={() => { setColumns(view.columns); setSort(view.sort); setRange(dateRange?.value ?? [null, null]); setOpen(true); }}>
                Filter · {label} {view.sort.dir === "asc" ? "↑" : "↓"}{dated ? " · dates" : ""}{hidden ? ` · ${hidden} hidden` : ""}
            </Button>
            <Modal opened={open} onClose={() => setOpen(false)} title={<Text fw={600}>Filter: Columns and sort</Text>} size="lg">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl">
                    <div>
                        <Group justify="space-between" mb="xs">
                            <Text size="sm" fw={600}>Columns</Text>
                            <Button size="compact-xs" variant="subtle" onClick={() => setColumns(ALL_COLUMNS)}>Show all</Button>
                        </Group>
                        <Stack gap={8}>
                            <Checkbox label="Reference" checked disabled />
                            <Checkbox.Group value={columns} onChange={(v) => setColumns(ALL_COLUMNS.filter((k) => v.includes(k)))}>
                                <Stack gap={8}>{COLUMNS.map((c) => <Checkbox key={c.key} value={c.key} label={c.label} />)}</Stack>
                            </Checkbox.Group>
                        </Stack>
                    </div>
                    <div>
                        <Text size="sm" fw={600} mb="xs">Sort by</Text>
                        <Radio.Group value={sort.field} onChange={(v) => setSort({ ...sort, field: v as SortField })}>
                            <Stack gap={8}>{SORT_FIELDS.map((f) => <Radio key={f.value} value={f.value} label={f.label} />)}</Stack>
                        </Radio.Group>
                        <Text size="sm" fw={600} mt="md" mb="xs">Order</Text>
                        <Radio.Group value={sort.dir} onChange={(v) => setSort({ ...sort, dir: v as SortDir })}>
                            <Group gap="lg"><Radio value="asc" label="Ascending" /><Radio value="desc" label="Descending" /></Group>
                        </Radio.Group>
                        {note && <Text size="xs" c="dimmed" mt="md">{note}</Text>}
                    </div>
                </SimpleGrid>
                {dateRange && (
                    <DatePickerInput mt="lg" type="range" allowSingleDateInRange clearable label="Created between" placeholder="Any date"
                        description="Kuwait days, both included" valueFormat="DD/MM/YYYY" maxDate={new Date()}
                        value={range} onChange={(v) => setRange([v[0] ?? null, v[1] ?? null])} />
                )}
                <Group justify="space-between" mt="xl">
                    <Button variant="subtle" color="gray" onClick={() => { setColumns(ALL_COLUMNS); setSort(view.defaultSort); setRange([null, null]); }}>Reset</Button>
                    <Group>
                        <Button variant="default" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={() => {
                            const sortChanged = !sameSort(sort, view.sort);
                            view.apply({ columns, sort });
                            if (dateRange && (range[0] !== dateRange.value[0] || range[1] !== dateRange.value[1])) dateRange.onChange(range);
                            onApply?.(sortChanged);
                            setOpen(false);
                        }}>Apply</Button>
                    </Group>
                </Group>
            </Modal>
        </>
    );
}

/** Badges in the table keep their full text: they never shrink into "AWAITING PURCHASE …". */
export const FULL_BADGE = { minWidth: "max-content", flexShrink: 0 } as const;

/** "New": Pending (checker) or Submitted (awaiting CTC acceptance): no dealer assigned yet. */
const NEW_STATUSES: ReadonlySet<string> = new Set([CtcPoStatusCode.waiting_customer_approval, CtcPoStatusCode.waiting_ctc_acceptance]);
export const isNewOrder = (po: CtcPurchaseOrder) => !po.assignedDealerEmail && NEW_STATUSES.has(po.tags?.poStatus?.code ?? "");

const STALE_AFTER_MS = 3 * 3600_000;
/**
 * In progress (accepted, not finished) and nothing changed for more than 3 hours: the hours since the last update,
 * else undefined. Draft, Pending, Submitted and finished orders (completed, rejected, deleted, cancelled) never are.
 */
export const staleHours = (po: CtcPurchaseOrder, now: number): number | undefined => {
    const code = po.tags?.poStatus?.code as CtcPoStatusCode | undefined;
    if (!code || code === CtcPoStatusCode.draft || NEW_STATUSES.has(code) || isTerminal(code)) return undefined;
    const updated = (po.updated as Stamp)?.toMillis?.() ?? (po.created as Stamp)?.toMillis?.();
    return updated !== undefined && now - updated > STALE_AFTER_MS ? Math.floor((now - updated) / 3600_000) : undefined;
};

const DAY_MS = 86400_000;
/** "YYYY-MM-DD" in Kuwait (UTC+3) of a time in ms. */
const kuwaitDay = (ms: number) => new Date(ms + 3 * 3600_000).toISOString().slice(0, 10);
const createdMs = (po: CtcPurchaseOrder) => (po.created as Stamp)?.toMillis?.();
/** Rejected, deleted and cancelled orders don't count as a second order (completed ones do). */
const isDead = (po: CtcPurchaseOrder) => {
    const code = po.tags?.poStatus?.code as CtcPoStatusCode | undefined;
    return !!code && isTerminal(code) && code !== CtcPoStatusCode.completed;
};
const sameDayKey = (po: CtcPurchaseOrder, ms: number) => `${po.customerId}|${po.beneficiary?.number}|${kuwaitDay(ms)}`;

/**
 * Possible duplicates: the same beneficiary (of the same customer) has more than one live order created on the same
 * Kuwait day. The other order may be on another page, so this looks them up in Firestore: one query per 30
 * beneficiaries shown (indexes beneficiary.number + created, and customerId + beneficiary.number + created).
 * Returns, per order id, the references of all that beneficiary's orders that day.
 */
const useSameDayDuplicates = (orders: CtcPurchaseOrder[]) => {
    const db = useFirestore();
    const user = useCtcUser();
    const [result, setResult] = useState<Map<string, string[]>>(new Map());
    const rows = orders.filter((po) => po.beneficiary?.number && createdMs(po) !== undefined && !isDead(po));
    const signature = rows.map((po) => `${po.id}:${po.beneficiary.number}:${createdMs(po)}`).join(",");
    useEffect(() => {
        if (!rows.length) { setResult(new Map()); return; }
        let cancelled = false;
        const times = rows.map((po) => createdMs(po)!);
        const start = ctcKuwaitDayEnd(Math.min(...times)).getTime() - DAY_MS; // start of the earliest day shown
        const end = ctcKuwaitDayEnd(Math.max(...times)).getTime(); // end of the latest day shown
        const numbers = [...new Set(rows.map((po) => po.beneficiary.number))];
        const chunks = Array.from({ length: Math.ceil(numbers.length / 30) }, (_, i) => numbers.slice(i * 30, i * 30 + 30));
        Promise.all(chunks.map((chunk) => getDocs(query(collection(db, "ctc_purchase_orders"),
            ...(user.customerId ? [where("customerId", "==", user.customerId)] : []),
            where("beneficiary.number", "in", chunk),
            where("created", ">=", Timestamp.fromMillis(start)),
            where("created", "<", Timestamp.fromMillis(end)),
            // Newest first matches the existing indexes (beneficiary.number + created desc, and with customerId);
            // without it Firestore sorts ascending and asks for another index
            orderBy("created", "desc")))))
            .then((snaps) => {
                if (cancelled) return;
                const groups = new Map<string, { id: string; ref: string }[]>();
                snaps.flatMap((s) => s.docs).forEach((d) => {
                    const po = { ...(d.data() as CtcPurchaseOrder), id: d.id };
                    const ms = createdMs(po);
                    if (ms === undefined || isDead(po)) return;
                    const key = sameDayKey(po, ms);
                    groups.set(key, [...(groups.get(key) ?? []), { id: d.id, ref: po.referenceNumber }]);
                });
                const next = new Map<string, string[]>();
                rows.forEach((po) => {
                    const group = groups.get(sameDayKey(po, createdMs(po)!)) ?? [];
                    if (group.length > 1) next.set(po.id!, group.map((g) => g.ref).sort());
                });
                setResult(next);
            })
            .catch((e) => {
                // The warning is a hint: without it the list still works. Say why in the console (missing index, rules…)
                console.warn("CTC same-day duplicate check failed", e);
                if (!cancelled) setResult(new Map());
            });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [db, signature, user.customerId]);
    return result;
};

/** » next step: shown only when the next step on this order is the user's (the same actions the order page offers, see ctcActionsFor); opens the order. */
function NextStepArrow({ poId }: { poId: string }) {
    return (
        <Tooltip label="Your step: open to act">
            <ActionIcon component={Link} to={`/ctc/orders/${poId}`} variant="transparent" size="lg" color="orange" aria-label="Your step: open the order">
                <IconChevronsRight size={22} stroke={2.2} />
            </ActionIcon>
        </Tooltip>
    );
}

/** Eye in a light rounded box: opens the order. */
function ViewOrder({ poId }: { poId: string }) {
    return (
        <Tooltip label="View order">
            <ActionIcon component={Link} to={`/ctc/orders/${poId}`} variant="light" color="blue" size="lg" radius="md" aria-label="View order">
                <IconEye size={18} />
            </ActionIcon>
        </Tooltip>
    );
}

/** One alert: an icon in a light tinted circle. */
function AlertDot({ label, color, icon }: { label: string; color: string; icon: ReactNode }) {
    return (
        <Tooltip multiline maw={320} label={label}>
            <ThemeIcon variant="light" color={color} radius="xl" size="lg" aria-label={label}>{icon}</ThemeIcon>
        </Tooltip>
    );
}

/** The current time, ticking every minute, so "more than 3 hours" shows up while a list stays open. */
const useNow = () => {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(t);
    }, []);
    return now;
};

export function OrdersTable({ orders, emptyText, loading, columns }: { orders: CtcPurchaseOrder[]; emptyText: string; loading: boolean; columns: ColumnKey[] }) {
    const show = (c: ColumnKey) => columns.includes(c);
    const now = useNow();
    const duplicates = useSameDayDuplicates(orders);
    const user = useCtcUser();
    // Customer settings (internal approval, who sells…) decide the next step, as on the order page
    const customers = useLiveCollection<CtcCustomer>("ctc_customers").data;
    const isMine = (po: CtcPurchaseOrder) =>
        ctcActionsFor(po, user, customers?.find((c) => c.id === po.customerId)).some((a) => CTC_NEXT_STEP_ACTIONS.has(a));
    return (
        // Full-width columns (badges never cut, references never wrap): scroll sideways on narrow screens
        <Table.ScrollContainer minWidth={1100} type="native">
        <Table striped highlightOnHover withTableBorder style={{ whiteSpace: "nowrap" }}>
            <Table.Thead>
                <Table.Tr>
                    <Table.Th>Reference</Table.Th>
                    <Table.Th w={110}>Alerts</Table.Th>
                    {COLUMNS.filter((c) => show(c.key)).map((c) => <Table.Th key={c.key}>{c.label}</Table.Th>)}
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {orders.map((po) => (
                    <Table.Tr key={po.id}>
                        <Table.Td>
                            {/* Eye · » (only on your step; the space is kept so references line up) · reference / client ref */}
                            <Group gap="sm" wrap="nowrap">
                                <ViewOrder poId={po.id!} />
                                <Box w={34} style={{ flexShrink: 0 }}>{isMine(po) && <NextStepArrow poId={po.id!} />}</Box>
                                <div>
                                    <Group gap={8} wrap="nowrap">
                                        <Text component={Link} to={`/ctc/orders/${po.id}`} fw={700} c="var(--mantine-color-text)" style={{ textDecoration: "none" }}>{po.referenceNumber}</Text>
                                        {isNewOrder(po) && <Tooltip label="No dealer assigned yet"><Badge color="blue" variant="filled" radius="xl" style={FULL_BADGE}>New</Badge></Tooltip>}
                                    </Group>
                                    {po.clientRefNo && <Text size="sm" c="dimmed">{po.clientRefNo}</Text>}
                                </div>
                            </Group>
                        </Table.Td>
                        {/* Possible duplicate and no-update warnings, side by side */}
                        <Table.Td>
                            <Group gap={8} wrap="nowrap" justify="flex-end">
                                {duplicates.has(po.id!) && (
                                    <AlertDot color="red" icon={<IconAlertTriangle size={18} />}
                                        label={`Possible duplicate: beneficiary ${po.beneficiary?.number} has ${duplicates.get(po.id!)!.length} orders on ${new Date(createdMs(po)! + 3 * 3600_000).toISOString().slice(0, 10).split("-").reverse().join("/")}: ${duplicates.get(po.id!)!.join(", ")}`} />
                                )}
                                {(() => {
                                    const hours = staleHours(po, now);
                                    return hours === undefined ? null : (
                                        <AlertDot color="yellow" icon={<IconClock size={18} />}
                                            label={`No update for ${hours >= 48 ? `${Math.floor(hours / 24)} days` : `${hours} hours`} (last ${formatStamp((po.updated ?? po.created) as Stamp)})`} />
                                    );
                                })()}
                            </Group>
                        </Table.Td>
                        {show("beneficiary") && <Table.Td>{po.beneficiary?.number}</Table.Td>}
                        {show("amount") && <Table.Td>{formatMoney(po.amount, po.currencyId)}</Table.Td>}
                        {show("status") && <Table.Td><TagBadge tag={ctcStatusTag(po.tags?.poStatus)} style={FULL_BADGE} /></Table.Td>}
                        {show("state") && <Table.Td>{po.state ?? "—"}</Table.Td>}
                        {show("lane") && <Table.Td>
                            {/* Who acts next, then each warning on its own line */}
                            <Stack gap={4} align="flex-start">
                                {po.lane ? <Badge variant="light" color={po.lane === "beneficiary" ? "green" : "gray"} style={FULL_BADGE}>{po.lane}</Badge> : null}
                                {po.lane === "beneficiary" && po.whatsappPrompt?.sentAt && Date.now() >= ctcKuwaitDayEnd(po.whatsappPrompt.sentAt.toMillis()).getTime() && <Badge color="orange" variant="light" style={FULL_BADGE}>Beneficiary question expired</Badge>}
                                {po.lane === "beneficiary" && !po.whatsappPrompt && <Badge color="red" variant="light" style={FULL_BADGE}>Question not sent</Badge>}
                                {!!po.pendingEffects?.length && <Badge color="orange" variant="outline" style={FULL_BADGE}>Automation pending</Badge>}
                            </Stack>
                        </Table.Td>}
                        {show("dealer") && <Table.Td>{po.assignedDealerEmail?.split("@")[0]}</Table.Td>}
                        {show("created") && <Table.Td><Tooltip label={po.makerEmail}><span>{formatStamp(po.created as Stamp)}</span></Tooltip></Table.Td>}
                        {show("updated") && <Table.Td>{po.updated ? <Tooltip label={po.updatedBy ?? ""} disabled={!po.updatedBy}><span>{formatStamp(po.updated as Stamp)}</span></Tooltip> : "—"}</Table.Td>}
                    </Table.Tr>
                ))}
                {!orders.length && !loading && <Table.Tr><Table.Td colSpan={columns.length + 2}><Text c="dimmed" size="sm">{emptyText}</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
    );
}

/** Lanes the Watch list shows: the user's own, plus WhatsApp-pending orders for dealers, makers and checkers; admins / viewers without a lane see every lane. */
export const useWatchLanes = (user: CtcUser) => useMemo<CtcRole[]>(() => {
    const mine = user.roles.filter((r): r is CtcRole => r !== "admin" && r !== "backoffice" && r !== "viewer");
    // WhatsApp-pending orders: dealers record answers; makers / checkers may request the transfer or sale there too
    if (user.has("dealer") || user.has("maker") || user.has("checker")) mine.push("beneficiary");
    // Admins and viewers with no lane of their own watch every lane (viewers: read only)
    return (user.isAdmin || user.has("viewer")) && mine.length === 0 ? ["maker", "checker", "supervisor", "dealer", "beneficiary"] : mine;
}, [user]);

export type CtcListBackend = "typesense" | "firestore";
const BACKEND_KEY = "ctcList:backend";

/**
 * Typesense (default) or Firestore for the Watch list, Purchase orders and Transactions, remembered in this
 * browser. Firestore when there is no CTC search key (or no collection configured for this project).
 */
export const useCtcListBackend = () => {
    const { ctcSearchKey } = useContext(MyUserDataContext);
    const available = !!ctcSearchKey && !!import.meta.env.VITE_CTC_TYPESENSE_ORDERS_COLLECTION && !!import.meta.env.VITE_CTC_TYPESENSE_SETTLEMENTS_COLLECTION;
    const [choice, setChoice] = useState<CtcListBackend>(() => readPref<CtcListBackend>(BACKEND_KEY, "typesense", (v) => v === "typesense" || v === "firestore"));
    const backend: CtcListBackend = available ? choice : "firestore";
    const set = (next: CtcListBackend) => { setChoice(next); writePref(BACKEND_KEY, next); };
    const control = (
        <SegmentedControl size="xs" value={backend} onChange={(v) => set(v as CtcListBackend)} data={[
            { label: "Firestore", value: "firestore" },
            { label: "Typesense", value: "typesense", disabled: !available },
        ]} />
    );
    return { backend, setBackend: set, control };
};
